import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, CircularProgress, TextField, InputAdornment, Button, TablePagination,
  Grid, MenuItem
} from '@mui/material';
import { Search, Refresh, PhoneCallback } from '@mui/icons-material';
import api from '../../services/api';

const STATUS_COLOR: any = { answered: 'success', failed: 'error', busy: 'warning', noanswer: 'default', cancelled: 'default' };

export const CustomerCDRPage: React.FC = () => {
  const [data, setData] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: '50', offset: String(page * 50) });
      if (search) p.set('dst', search);
      if (status) p.set('status', status);
      const { data: res } = await api.get(`/cdr?${p}`);
      setData(res.data || []);
      setTotal(res.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const fmt = (secs: number) => {
    if (!secs) return '0:00';
    const m = Math.floor(secs / 60), s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PhoneCallback sx={{ color: 'primary.main', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Call Detail Records</Typography>
            <Typography variant="body2" color="text.secondary">{total} calls — your account only</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField size="small" placeholder="Search by number..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
            sx={{ width: 200 }} />
          <TextField select size="small" value={status} onChange={e => setStatus(e.target.value)} sx={{ width: 130 }}>
            <MenuItem value="">All Status</MenuItem>
            {['answered', 'failed', 'busy', 'noanswer'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
        </Box>
      </Box>

      <Card>
        {loading ? <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box> : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Date / Time', 'From', 'To', 'Duration', 'Cost', 'Status'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: 12 }}>
                      {row.start_time ? new Date(row.start_time).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.src_number || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{row.dst_number || '—'}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(row.duration || 0)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      ${Number(row.cost || 0).toFixed(4)}
                    </TableCell>
                    <TableCell>
                      <Chip label={row.status || 'unknown'} size="small" color={STATUS_COLOR[row.status] || 'default'} />
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No call records found.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={total} page={page} rowsPerPage={50}
              onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[50]} />
          </>
        )}
      </Card>
    </Box>
  );
};
